import { ApiService } from "../shared/utils/ApiService.js";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { Navbar } from "../shared/components/navbar/Navbar.js";
import { AuthService } from "../shared/services/AuthService.js";
import { AppConfig } from "../shared/utils/AppConfig.js";
import { escapeHtml } from "../shared/utils/sanitize.js";
import { uploadFile } from "../shared/services/KbApiService.js";
import {
  tipoForFile, esArchivoDeContenido, fileExtension, iconForFile, formatSize, estadoAdjunto, buildAdjuntosPayload, isYoutubeUrl,
} from "../shared/utils/adjuntos.js";
import {
  moverItem, subcategoriaDeArticulo, rutaArticuloHtml, ordenarPorCategoria, coincideFiltroArticulo, SIN_SUBCATEGORIA,
} from "../shared/utils/subcategorias.js";

import { TourService } from "../shared/utils/TourService.js";
import { relacionadosDe, relacionadosInversos, planRelacionados } from "../shared/utils/relacionados.js";
import adminTemplate from "./templates/admin.html?raw";
import Swal from "sweetalert2";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import mammoth from "mammoth";
import { marked } from "marked";

const TIPO_LABELS = { texto: "Texto", pdf: "PDF", video: "Video", imagen: "Imagen", audio: "Audio" };

// Sube un archivo (Worker real o mock). El Worker extrae el texto y devuelve el adjunto.
async function subirArchivo(file) {
  const max = AppConfig.limits.maxFileSize;
  if (file.size > max) throw new Error(`Supera el máximo de ${formatSize(max)}`);
  if (AppConfig.api.useMock) {
    return (await ApiService.post("admin/upload", { nombre: file.name, mime: file.type, tamano: file.size })).data;
  }
  return uploadFile(file);
}

async function cargarTextoAdjunto(id) {
  const resp = await ApiService.post("admin/adjunto", { id });
  return resp?.data?.texto || "";
}

// Archivo guardado en R2 (o simulado en mock), a diferencia de un enlace externo como YouTube
function esArchivoPropio(adj) {
  return !!adj && (String(adj.url).includes("/files/") || String(adj.url).startsWith("uploads/"));
}

function _relativeTime(isoString) {
  if (!isoString) return "Nunca";
  // SQLite datetime('now') → "2026-03-18 17:00:00" (sin Z, con espacio)
  // JS toISOString()       → "2026-03-18T17:00:00.000Z" (ya tiene Z)
  const normalized = isoString.endsWith("Z")
    ? isoString
    : isoString.replace(" ", "T") + "Z";
  const diff = Date.now() - new Date(normalized).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "Hace un momento";
  if (mins < 60) return `Hace ${mins} min`;
  if (hours < 24) return `Hace ${hours} h`;
  if (days < 30) return `Hace ${days} día${days > 1 ? "s" : ""}`;
  return new Date(normalized).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

function extractYoutubeId(urlOrId) {
  if (!urlOrId) return null;
  if (/^[a-zA-Z0-9_-]{7,12}$/.test(urlOrId.trim())) return urlOrId.trim();
  try {
    const url = new URL(urlOrId);
    if (url.hostname.includes("youtu.be")) return url.pathname.slice(1).split("?")[0];
    return url.searchParams.get("v");
  } catch {
    return null;
  }
}

const COLORES = ["primary", "success", "warning", "info", "danger"];
const ICONOS_CAT = [
  "fa-screwdriver-wrench", "fa-chart-line", "fa-building", "fa-users",
  "fa-gears", "fa-book", "fa-laptop", "fa-headset",
];

export const AdminController = {

  render: async() => {
    try {
      const user = AuthService.getCurrentUser();
      if (!AuthService.puedeGestionar(user)) {
        window.location.hash = "dashboard";
        return;
      }

      // Un editor solo gestiona el contenido de sus categorías: el Worker le devuelve
      // esos artículos y categorías, y no le permite usuarios ni sugerencias.
      const editor = AuthService.isEditor(user);
      const [artsResp, catsResp, usersResp, sugResp] = await Promise.all([
        ApiService.post("admin/articulos"),
        ApiService.post("admin/categorias"),
        editor ? null : ApiService.post("admin/usuarios"),
        editor ? null : ApiService.post("admin/sugerencias"),
      ]);

      const articulos = artsResp?.data || [];
      const categorias = catsResp?.data || [];
      const usuarios = usersResp?.data || [];
      const sugerencias = sugResp?.data || [];

      const catMap = Object.fromEntries(categorias.map(c => [c.id, c]));
      const totalArts = articulos.length;

      const categoriasWithMeta = categorias.map(c => ({
        ...c,
        esPublica: !!c.es_publica,
        isApi: c.tipo === 'api',
        subcatsBadge: AppConfig.features.subcategories && c.subcategorias?.length
          ? `<span class="badge bg-light text-muted border me-1"><i class="fas fa-folder-tree me-1"></i>${c.subcategorias.length} subcat.</span>`
          : "",
        count: articulos.filter(a => a.categoriaId === c.id).length,
        pct: Math.round((articulos.filter(a => a.categoriaId === c.id).length / (totalArts || 1)) * 100),
      }));

      // Agrupados por categoría y subcategoría (el Worker los manda por fecha)
      const artsWithMeta = ordenarPorCategoria(articulos, categorias).map(a => ({
        ...a,
        tipoLabel: TIPO_LABELS[a.tipo] || a.tipo,
        rutaHtml: catMap[a.categoriaId]
          ? rutaArticuloHtml(
            catMap[a.categoriaId].nombre,
            AppConfig.features.subcategories ? subcategoriaDeArticulo(catMap[a.categoriaId], a) : ""
          )
          : "—",
        ultimaConsultaRelativa: _relativeTime(a.ultimaConsulta),
      }));

      const usuariosWithMeta = usuarios.map(u => ({
        ...u,
        // nombre/email son editables por el propio usuario -> escapar para evitar XSS (C2)
        nombre: escapeHtml(u.nombre),
        // Los usuarios de un proveedor externo pueden no tener email: se muestra su usuario
        email: escapeHtml(u.email || u.usuario || ""),
        isAdmin: u.rol === "admin",
        estadoBadge: u.activo === false
          ? `<span class="badge bg-secondary">Inactivo</span>`
          : `<span class="badge bg-success">Activo</span>`,
        // Las categorías que además edita llevan un lápiz
        categoriasHtml: u.categorias === null
          ? `<span class="badge bg-light text-dark border small">Todas</span>`
          : (u.categorias || [])
              .filter(cid => catMap[cid])
              .map(cid => (u.categoriasEditables || []).includes(cid)
                ? `<span class="badge bg-warning-subtle text-dark border border-warning small" title="Edita esta categoría"><i class="fas fa-pen me-1"></i>${escapeHtml(catMap[cid].nombre)}</span>`
                : `<span class="badge bg-light text-dark border small">${escapeHtml(catMap[cid].nombre)}</span>`)
              .join(""),
      }));

      const sugerenciasHtml = sugerencias.map(s => {
        const fecha = _relativeTime(s.fecha);
        const tipoBadge = s.tipo === "ai"
          ? `<span class="badge bg-primary">IA</span>`
          : `<span class="badge bg-secondary">Manual</span>`;
        // texto y usuario_nombre son controlables por cualquier usuario -> escapar (C2)
        const textoCorto = escapeHtml(s.texto.length > 80 ? s.texto.substring(0, 80) + "…" : s.texto);
        return `<tr>
          <td><span class="fw-semibold">${escapeHtml(s.usuario_nombre)}</span></td>
          <td><span class="text-muted small">${textoCorto}</span></td>
          <td class="d-none d-md-table-cell">${tipoBadge}</td>
          <td class="d-none d-md-table-cell"><span class="text-muted small">${fecha}</span></td>
          <td class="text-end">
            <button class="btn btn-sm btn-outline-secondary me-1 btn-ver-sugerencia" data-id="${s.id}" data-texto="${encodeURIComponent(s.texto)}" data-nombre="${encodeURIComponent(s.usuario_nombre)}" data-tipo="${s.tipo}" data-fecha="${s.fecha}">
              <i class="fas fa-eye"></i>
            </button>
            <button class="btn btn-sm btn-outline-danger btn-del-sugerencia" data-id="${s.id}">
              <i class="fas fa-trash"></i>
            </button>
          </td>
        </tr>`;
      }).join("");

      const data = {
        totalArticulos: articulos.length,
        totalCategorias: categorias.length,
        totalUsuarios: usuarios.length,
        totalSugerencias: sugerencias.length,
        articulos: artsWithMeta,
        // Para un editor, `categorias` ya son solo las que edita
        filtroCatsOptions: categorias
          .filter(c => c.tipo !== "api")
          .map(c => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.nombre)}</option>`)
          .join(""),
        categorias: categoriasWithMeta,
        usuarios: usuariosWithMeta,
        sugerenciasHtml,
        noSugerencias: sugerencias.length === 0,
      };

      const content = TemplateEngine.render(adminTemplate, data);
      document.getElementById("app").innerHTML = await Navbar.render() + content;
      if (editor) AdminController._aplicarModoEditor(categorias);

      AdminController._bindTabs();
      AdminController._bindArticulosActions(categorias);
      AdminController._bindFiltroArticulos(articulos, categorias);
      AdminController._bindCatActions();
      AdminController._bindUserActions(categorias);
      AdminController._bindSugerenciasActions();
      TourService.resumeIfPending();
    } catch (error) {
      // El detalle del error queda solo en consola (diagnóstico); a la UI va un
      // mensaje genérico para no filtrar internals del backend (hallazgo B12).
      console.error("Admin error:", error);
      document.getElementById("app").innerHTML =
        await Navbar.render() +
        `<div class="container mt-4"><div class="alert alert-danger">No se pudo cargar el panel de administración. Intentá recargar la página.</div></div>`;
    }
  },

  // Panel reducido para un editor: artículos y, con subcategorías activas, las
  // subcategorías de sus categorías. Sin usuarios, sugerencias ni alta de categorías.
  _aplicarModoEditor: (categorias) => {
    const titulo = document.querySelector(".kb-admin h4");
    if (titulo) titulo.innerHTML = '<i class="fas fa-pen-to-square text-primary me-2"></i>Gestión de contenido';
    const subtitulo = document.querySelector(".kb-admin h4 + p");
    if (subtitulo) subtitulo.textContent = `Editás: ${categorias.map(c => c.nombre).join(", ")}`;

    const quitarTab = (tab) => {
      document.querySelector(`[data-tab="${tab}"]`)?.closest("li")?.remove();
      document.getElementById(`tab-${tab}`)?.remove();
    };
    quitarTab("usuarios");
    quitarTab("sugerencias");
    if (!AppConfig.features.subcategories) {
      quitarTab("categorias");
      return;
    }
    document.getElementById("btn-new-categoria")?.remove();
    document.querySelectorAll(".btn-edit-cat").forEach(btn => {
      btn.title = "Subcategorías";
      btn.innerHTML = '<i class="fas fa-folder-tree"></i>';
    });
  },

  // Editor: solo las subcategorías de la categoría (los datos de la categoría son del admin)
  _showSubcatsForm: async(cat) => {
    let subs = [...(cat.subcategorias || [])];
    let cambiaron = false;
    await Swal.fire({
      titleText: `Subcategorías de ${cat.nombre}`,
      html: `
        <div class="text-start">
          <div class="form-text mt-0 mb-2">Los cambios se guardan al momento.</div>
          <div id="sc-subs-list"></div>
          <div class="input-group input-group-sm mt-2">
            <input id="sc-sub-nueva" class="form-control" placeholder="Nueva subcategoría (ej. Procedimientos)">
            <button type="button" id="sc-sub-agregar" class="btn btn-outline-primary"><i class="fas fa-plus me-1"></i>Agregar</button>
          </div>
          <small id="sc-subs-status" class="d-block mt-1"></small>
        </div>`,
      confirmButtonText: "Listo",
      didOpen: () => AdminController._bindSubcatsEditor(cat.id, () => subs, (v) => { subs = v; cambiaron = true; }),
    });
    if (cambiaron) AdminController.render();
  },

  _bindSugerenciasActions: () => {
    document.querySelectorAll(".btn-ver-sugerencia").forEach(btn => {
      btn.addEventListener("click", () => {
        const texto = decodeURIComponent(btn.dataset.texto);
        const nombre = decodeURIComponent(btn.dataset.nombre);
        const tipo = btn.dataset.tipo === "ai" ? "Consulta a la IA" : "Sugerencia manual";
        const fecha = _relativeTime(btn.dataset.fecha);
        Swal.fire({
          title: "Sugerencia de tema",
          html: `<div class="text-start">
            <p class="mb-1"><strong>Usuario:</strong> ${escapeHtml(nombre)}</p>
            <p class="mb-1"><strong>Origen:</strong> ${tipo}</p>
            <p class="mb-3"><strong>Fecha:</strong> ${fecha}</p>
            <div class="p-3 bg-light rounded">${escapeHtml(texto)}</div>
          </div>`,
          confirmButtonText: "Cerrar",
          showCancelButton: false,
        });
      });
    });

    document.querySelectorAll(".btn-del-sugerencia").forEach(btn => {
      btn.addEventListener("click", async() => {
        const result = await Swal.fire({
          title: "¿Eliminar sugerencia?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        });
        if (!result.isConfirmed) return;
        await ApiService.post("admin/sugerencia/eliminar", { id: btn.dataset.id });
        btn.closest("tr")?.remove();
        const tbody = document.getElementById("admin-sugerencias-tbody");
        if (tbody && !tbody.querySelector("tr")) {
          tbody.closest(".table-responsive")?.replaceWith(
            Object.assign(document.createElement("div"), {
              className: "text-center py-5 text-muted",
              innerHTML: '<i class="fas fa-lightbulb fa-3x mb-3 d-block opacity-25"></i><p class="mb-0">No hay sugerencias pendientes</p>',
            })
          );
        }
      });
    });
  },

  // Filtro del listado de artículos. Se recuerda en la sesión para no perderlo cada vez que
  // el panel se vuelve a dibujar (por ejemplo, después de guardar un artículo).
  _FILTRO_KEY: "kb_admin_filtro_articulos",

  _leerFiltro: () => {
    try {
      return JSON.parse(sessionStorage.getItem(AdminController._FILTRO_KEY)) || {};
    } catch {
      return {};
    }
  },

  _guardarFiltro: (filtro) => {
    try {
      sessionStorage.setItem(AdminController._FILTRO_KEY, JSON.stringify(filtro));
    } catch { /* sin storage el filtro solo dura hasta el próximo render */ }
  },

  _bindFiltroArticulos: (articulos, categorias) => {
    const catSel = document.getElementById("filtro-art-cat");
    const subSel = document.getElementById("filtro-art-sub");
    const subGroup = document.getElementById("filtro-art-sub-group");
    const textoInput = document.getElementById("filtro-art-texto");
    const count = document.getElementById("filtro-art-count");
    const vacio = document.getElementById("filtro-art-vacio");
    if (!catSel) return;
    const porId = new Map(articulos.map(a => [String(a.id), a]));

    const fillSubs = (seleccionada) => {
      const cat = categorias.find(c => c.id === catSel.value);
      const subs = AppConfig.features.subcategories ? (cat?.subcategorias || []) : [];
      subGroup.classList.toggle("d-none", !subs.length);
      const sueltos = articulos.some(a => a.categoriaId === catSel.value && !a.subcategoriaId);
      subSel.innerHTML = '<option value="">Todas las subcategorías</option>' +
        subs.map(s => `<option value="${escapeHtml(s.id)}">${escapeHtml(s.nombre)}</option>`).join("") +
        (sueltos ? `<option value="${SIN_SUBCATEGORIA}">(sin subcategoría)</option>` : "");
      subSel.value = subs.length && [...subSel.options].some(o => o.value === seleccionada) ? seleccionada : "";
    };

    const aplicar = () => {
      const filtro = { cat: catSel.value, sub: subGroup.classList.contains("d-none") ? "" : subSel.value, texto: textoInput.value };
      AdminController._guardarFiltro(filtro);
      let visibles = 0;
      document.querySelectorAll("#admin-articulos-tbody tr").forEach(tr => {
        const art = porId.get(tr.dataset.id);
        const ok = !art || coincideFiltroArticulo(art, filtro);
        tr.classList.toggle("d-none", !ok);
        if (ok) visibles++;
      });
      const hayFiltro = filtro.cat || filtro.texto.trim();
      count.textContent = hayFiltro ? `${visibles} de ${articulos.length} artículos` : `${articulos.length} artículos`;
      vacio.classList.toggle("d-none", visibles > 0 || !articulos.length);
    };

    // Restaurar el filtro anterior (si la categoría ya no está, vuelve a "todas")
    const previo = AdminController._leerFiltro();
    catSel.value = [...catSel.options].some(o => o.value === previo.cat) ? previo.cat : "";
    textoInput.value = previo.texto || "";
    fillSubs(previo.sub || "");
    aplicar();

    catSel.addEventListener("change", () => { fillSubs(""); aplicar(); });
    subSel.addEventListener("change", aplicar);
    textoInput.addEventListener("input", aplicar);
  },

  _bindTabs: () => {
    document.querySelectorAll("[data-tab]").forEach(btn => {
      btn.addEventListener("click", () => {
        document.querySelectorAll("[data-tab]").forEach(b => b.classList.remove("active"));
        document.querySelectorAll(".admin-tab-content").forEach(t => t.classList.add("d-none"));
        btn.classList.add("active");
        document.getElementById(`tab-${btn.dataset.tab}`)?.classList.remove("d-none");
      });
    });
  },

  _bindArticulosActions: (categorias) => {
    // New article
    document.getElementById("btn-new-articulo")?.addEventListener("click", () => {
      const catsDisponibles = categorias.filter(c => c.tipo !== 'api');
      if (!catsDisponibles.length) {
        Swal.fire({ icon: "info", title: "Sin categorías disponibles", text: "Todas las categorías existentes son de tipo API y no admiten artículos. Creá una categoría estándar primero.", confirmButtonText: "Entendido" });
        return;
      }
      // Con un filtro activo, el artículo nuevo arranca en esa categoría/subcategoría
      const { cat, sub } = AdminController._leerFiltro();
      const subcategoriaId = sub && sub !== SIN_SUBCATEGORIA ? sub : null;
      AdminController._showArticuloForm(null, categorias, { categoriaId: cat || null, subcategoriaId });
    });

    // Edit
    document.querySelectorAll(".btn-edit-articulo").forEach(btn => {
      btn.addEventListener("click", async() => {
        const resp = await ApiService.post("kb/articulo", { id: btn.dataset.id });
        if (resp?.data) AdminController._showArticuloForm(resp.data, categorias);
      });
    });

    // Delete
    document.querySelectorAll(".btn-delete-articulo").forEach(btn => {
      btn.addEventListener("click", async() => {
        const result = await Swal.fire({
          title: "¿Eliminar artículo?",
          text: `"${btn.dataset.titulo}" será eliminado permanentemente.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        });
        if (!result.isConfirmed) return;
        await ApiService.post("admin/articulo/eliminar", { id: btn.dataset.id });
        Swal.fire({ icon: "success", title: "Artículo eliminado", toast: true, position: "top-end", timer: 2000, showConfirmButton: false });
        AdminController.render();
      });
    });
  },

  // preset: categoría y subcategoría iniciales de un artículo nuevo (las del filtro del listado)
  _showArticuloForm: async(art, categorias, preset = {}) => {
    const currentUser = AuthService.getCurrentUser();
    // Las categorías tipo API no admiten artículos
    const catsParaArticulos = categorias.filter(c => c.tipo !== 'api');
    const catInicial = art?.categoriaId ?? preset.categoriaId;
    const catsOptions = catsParaArticulos.map(c =>
      `<option value="${c.id}" ${catInicial === c.id ? "selected" : ""}>${c.nombre}</option>`
    ).join("");

    // ── Archivos del artículo ─────────────────────────────────────────────────
    // principal: adjunto que representa el tipo (PDF, imagen, audio, video o enlace)
    // adicionales: filas { key, file?, status: 'uploading'|'ok'|'error', errorMsg?, adj?, texto?, textoOriginal?, abierto? }
    let principal = null;
    let principalTexto = null; // texto del principal tal como está guardado (para detectar ediciones)
    let principalUploading = false;
    let adicionales = [];
    let tmpKey = 0;

    if (art) {
      const resp = await ApiService.post("admin/articulo/ver", { id: art.id });
      const adjs = resp?.data?.adjuntos || [];
      principal = adjs.find(a => a.esPrincipal) || null;
      adicionales = adjs.filter(a => !a.esPrincipal).map(adj => ({ key: adj.id, status: "ok", adj }));
      if (principal) principalTexto = principal.tieneTexto ? await cargarTextoAdjunto(principal.id) : "";
    }

    const principalEsArchivo = esArchivoPropio(principal);
    const urlInicial = principal && !principalEsArchivo ? principal.url : "";

    let quill = null;

    const EMOJIS = [
      "😀", "😊", "😂", "😍", "🤔", "😅", "😎", "🙁",
      "👍", "👎", "👏", "🙏", "💪", "👀", "🤝", "✌️",
      "❤️", "🔥", "✅", "❌", "⚠️", "⭐", "💡", "📌",
      "🎯", "📊", "📝", "📅", "🔑", "🛠️", "💬", "🚀", "🏆", "🎉",
    ];

    const emojiButtons = EMOJIS.map(e =>
      `<button type="button" class="swal-emoji-btn" data-emoji="${e}">${e}</button>`
    ).join("");

    const { value: formValues } = await Swal.fire({
      title: art ? "Editar artículo" : "Nuevo artículo",
      width: 720,
      html: `
        <div class="text-start">
          <div class="mb-3">
            <label class="form-label fw-semibold">Título</label>
            <input id="swal-titulo" class="form-control" value="${art?.titulo || ""}">
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Descripción</label>
            <textarea id="swal-desc" class="form-control" rows="2">${art?.descripcion || ""}</textarea>
          </div>
          <div class="row mb-3">
            <div class="col">
              <label class="form-label fw-semibold">Categoría</label>
              <select id="swal-cat" class="form-select">${catsOptions}</select>
            </div>
            <div class="col">
              <label class="form-label fw-semibold">Tipo</label>
              <select id="swal-tipo" class="form-select">
                <option value="texto"  ${art?.tipo === "texto" ? "selected" : ""}>Texto</option>
                <option value="pdf"    ${art?.tipo === "pdf" ? "selected" : ""}>PDF</option>
                <option value="video"  ${art?.tipo === "video" ? "selected" : ""}>Video</option>
                <option value="imagen" ${art?.tipo === "imagen" ? "selected" : ""}>Imagen</option>
                <option value="audio"  ${art?.tipo === "audio" ? "selected" : ""}>Audio</option>
              </select>
            </div>
          </div>
          <div class="mb-3" id="swal-subcat-group" style="display:none">
            <label class="form-label fw-semibold">Subcategoría</label>
            <select id="swal-subcat" class="form-select"></select>
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Autor</label>
            <input id="swal-autor" class="form-control" value="${art?.autor || currentUser?.nombre || ""}">
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Tags</label>
            <div id="swal-tags-container" class="form-control d-flex flex-wrap gap-1 align-items-center" style="min-height:38px;cursor:text;" tabindex="0">
              <input id="swal-tags-input" type="text" style="border:none;outline:none;min-width:100px;flex:1;" placeholder="Escribir y presionar Enter…">
            </div>
            <input type="hidden" id="swal-tags-value" value="[]">
            <div class="form-text">Presioná Enter o coma para agregar un tag.</div>
          </div>

          <!-- Tipo texto: editor WYSIWYG -->
          <div class="mb-3" id="swal-contenido-group">
            <label class="form-label fw-semibold">Contenido</label>
            <div id="swal-quill-toolbar">
              <span class="ql-formats">
                <select class="ql-header">
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option selected></option>
                </select>
              </span>
              <span class="ql-formats">
                <button class="ql-bold"></button>
                <button class="ql-italic"></button>
                <button class="ql-underline"></button>
                <button class="ql-strike"></button>
              </span>
              <span class="ql-formats">
                <button class="ql-list" value="ordered"></button>
                <button class="ql-list" value="bullet"></button>
              </span>
              <span class="ql-formats">
                <button class="ql-blockquote"></button>
                <button class="ql-link"></button>
                <button class="ql-image"></button>
              </span>
              <span class="ql-formats">
                <button id="swal-emoji-toggle" type="button" class="ql-emoji-toggle" title="Emojis">😊</button>
              </span>
              <span class="ql-formats">
                <button class="ql-clean"></button>
              </span>
            </div>
            <div id="swal-emoji-panel" class="swal-emoji-panel" style="display:none">${emojiButtons}</div>
            <div id="swal-quill-editor"></div>
            <div id="swal-txt-drop" class="kb-adj-drop mt-2">
              <i class="fas fa-cloud-arrow-up me-1"></i>Arrastrá un documento acá o
              <label for="swal-txt-file" class="kb-adj-pick mb-0">elegilo</label>
              para cargarlo en el contenido
              <input id="swal-txt-file" type="file" accept=".txt,.md,.markdown,.html,.htm,.docx" class="d-none">
              <div class="small text-muted mt-1">Markdown, texto, HTML o Word (.docx). Reemplaza lo que haya en el editor.</div>
              <small id="swal-txt-fname" class="d-block text-muted fst-italic"></small>
            </div>
            <div class="mt-2">
              <button type="button" id="swal-mejorar-btn" class="btn btn-outline-primary btn-sm">
                <i class="fas fa-wand-magic-sparkles me-1"></i>Mejorar redacción con IA
              </button>
            </div>
            <div id="swal-mejorar-preview" style="display:none" class="mt-2 border rounded p-3 bg-light">
              <small class="fw-semibold text-muted d-block mb-2">
                <i class="fas fa-robot me-1"></i>Versión mejorada — revisá antes de aceptar:
              </small>
              <div id="swal-mejorar-texto" class="small mb-3"
                style="white-space:pre-wrap; max-height:180px; overflow-y:auto; color:#1a1a1a;"></div>
              <div class="d-flex gap-2">
                <button type="button" id="swal-mejorar-aceptar" class="btn btn-success btn-sm">
                  <i class="fas fa-check me-1"></i>Aceptar
                </button>
                <button type="button" id="swal-mejorar-descartar" class="btn btn-outline-secondary btn-sm">
                  <i class="fas fa-xmark me-1"></i>Descartar
                </button>
              </div>
            </div>
          </div>

          <!-- Tipos no-texto: URL o Subir archivo -->
          <div class="mb-3" id="swal-recurso-group" style="display:none">
            <label class="form-label fw-semibold">Recurso</label>
            <div class="d-flex gap-3 mb-2">
              <div class="form-check">
                <input class="form-check-input" type="radio" name="swal-modo" id="swal-modo-url" value="url"
                  ${!principalEsArchivo ? "checked" : ""}>
                <label class="form-check-label" for="swal-modo-url">Ingresar URL</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" name="swal-modo" id="swal-modo-upload" value="upload"
                  ${principalEsArchivo ? "checked" : ""}>
                <label class="form-check-label" for="swal-modo-upload">Subir archivo</label>
              </div>
            </div>
            <div id="swal-url-wrap">
              <input id="swal-url" class="form-control" value="${escapeHtml(urlInicial)}"
                placeholder="URL del recurso o ID de YouTube">
              <button type="button" id="swal-yt-transcript-btn" class="btn btn-outline-secondary btn-sm mt-2" style="display:none">
                <i class="fas fa-closed-captioning me-1"></i>Obtener transcripción YouTube
              </button>
            </div>
            <div id="swal-file-wrap" style="display:none">
              <input id="swal-file" type="file" class="form-control">
              <small id="swal-upload-status" class="d-block mt-1" style="display:none!important"></small>
              <div id="swal-principal-actual"></div>
            </div>
          </div>
          <div id="swal-transcripcion-group" style="display:none" class="mt-3">
            <label class="form-label fw-semibold">
              <i class="fas fa-file-lines me-1"></i>Contenido para búsqueda (RAG)
            </label>
            <textarea id="swal-transcripcion" class="form-control small" rows="5"
              placeholder="Transcripción o texto extraído — podés editarlo antes de guardar"></textarea>
            <small class="text-muted">Este texto se indexa para el chat IA.</small>
          </div>

          <!-- Archivos adicionales: cualquier tipo, cada uno con su texto indexado -->
          <div class="mt-3">
            <label class="form-label fw-semibold"><i class="fas fa-paperclip me-1"></i>Archivos adicionales</label>
            <div id="swal-adj-drop" class="kb-adj-drop">
              <i class="fas fa-cloud-arrow-up me-1"></i>Arrastrá archivos acá o
              <label for="swal-adj-input" class="kb-adj-pick mb-0">elegilos</label>
              <input id="swal-adj-input" type="file" multiple class="d-none">
              <div class="small text-muted mt-1">PDF, Word, Markdown, texto, imágenes, audio. Máximo ${formatSize(AppConfig.limits.maxFileSize)} por archivo.</div>
            </div>
            <div id="swal-adj-list" class="mt-2"></div>
          </div>
        </div>
      `,
      didOpen: () => {
        // ── Tags chips ───────────────────────────────────────────────────────
        const tagsInput = document.getElementById("swal-tags-input");
        const tagsContainer = document.getElementById("swal-tags-container");
        const tagsHidden = document.getElementById("swal-tags-value");
        tagsHidden.value = JSON.stringify(art?.tags || []);

        const getTags = () => JSON.parse(tagsHidden.value || "[]");
        const setTags = (arr) => { tagsHidden.value = JSON.stringify(arr); };

        const renderChips = () => {
          tagsContainer.querySelectorAll(".kb-tag-chip").forEach(c => c.remove());
          getTags().forEach(tag => {
            const chip = document.createElement("span");
            chip.className = "kb-tag-chip badge bg-secondary d-flex align-items-center gap-1";
            chip.innerHTML = `${tag} <button type="button" class="btn-close btn-close-white" style="font-size:.5rem;" data-tag="${tag}"></button>`;
            chip.querySelector("button").addEventListener("click", () => {
              setTags(getTags().filter(t => t !== tag));
              renderChips();
            });
            tagsContainer.insertBefore(chip, tagsInput);
          });
        };

        const addTag = () => {
          const val = tagsInput.value.replace(/,/g, "").trim().toLowerCase();
          if (!val) return;
          const current = getTags();
          if (!current.includes(val)) { setTags([...current, val]); renderChips(); }
          tagsInput.value = "";
        };

        tagsInput.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
          if (e.key === "Backspace" && !tagsInput.value) {
            const tags = getTags();
            if (tags.length) { setTags(tags.slice(0, -1)); renderChips(); }
          }
        });
        tagsInput.addEventListener("blur", addTag);
        tagsContainer.addEventListener("click", () => tagsInput.focus());

        renderChips(); // inicializar con los tags existentes

        // ── Subcategoría: opciones según la categoría elegida ────────────────
        const catSelect = document.getElementById("swal-cat");
        const fillSubcats = (seleccionada) => {
          const subs = AppConfig.features.subcategories
            ? (categorias.find(c => c.id === catSelect.value)?.subcategorias || [])
            : [];
          document.getElementById("swal-subcat-group").style.display = subs.length ? "" : "none";
          document.getElementById("swal-subcat").innerHTML =
            '<option value="">(sin subcategoría)</option>' +
            subs.map(s => `<option value="${escapeHtml(s.id)}" ${s.id === seleccionada ? "selected" : ""}>${escapeHtml(s.nombre)}</option>`).join("");
        };
        catSelect.addEventListener("change", () => fillSubcats(null));
        fillSubcats(art ? art.subcategoriaId || null : preset.subcategoriaId || null);

        const tipoSelect = document.getElementById("swal-tipo");

        const initEditor = () => {
          if (quill) return;
          quill = new Quill("#swal-quill-editor", {
            theme: "snow",
            modules: {
              toolbar: {
                container: "#swal-quill-toolbar",
                handlers: {
                  image: () => {
                    const url = prompt("URL de la imagen:");
                    if (url) {
                      const range = quill.getSelection() ?? { index: 0 };
                      quill.insertEmbed(range.index, "image", url);
                    }
                  },
                },
              },
            },
          });

          if (art?.contenido) {
            quill.clipboard.dangerouslyPasteHTML(art.contenido);
          }

          // El focus trap de Swal2 no reconoce contenteditable — forzar foco al clickear
          document.getElementById("swal-quill-editor").addEventListener("click", () => quill.focus());

          // Emoji toggle
          document.getElementById("swal-emoji-toggle")?.addEventListener("click", () => {
            const panel = document.getElementById("swal-emoji-panel");
            panel.style.display = panel.style.display === "none" ? "" : "none";
          });

          document.querySelectorAll(".swal-emoji-btn").forEach(btn => {
            btn.addEventListener("click", () => {
              const emoji = btn.dataset.emoji;
              const range = quill.getSelection(true);
              quill.insertText(range.index, emoji, "user");
              quill.setSelection(range.index + emoji.length);
              document.getElementById("swal-emoji-panel").style.display = "none";
            });
          });

          // Mejorar redacción con IA
          document.getElementById("swal-mejorar-btn")?.addEventListener("click", async() => {
            const html = quill.root.innerHTML;
            if (!html || html === "<p><br></p>") return;
            const btn = document.getElementById("swal-mejorar-btn");
            btn.disabled = true;
            btn.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>Mejorando...';
            document.getElementById("swal-mejorar-preview").style.display = "none";
            try {
              const resp = await ApiService.post("admin/articulo/mejorar", { contenido: html });
              const textoMejorado = resp?.texto;
              if (textoMejorado) {
                document.getElementById("swal-mejorar-texto").textContent = textoMejorado;
                document.getElementById("swal-mejorar-preview").style.display = "";
                document.getElementById("swal-mejorar-aceptar").onclick = () => {
                  const htmlMejorado = textoMejorado.split("\n").filter(p => p.trim()).map(p => `<p>${p}</p>`).join("");
                  quill.clipboard.dangerouslyPasteHTML(htmlMejorado);
                  document.getElementById("swal-mejorar-preview").style.display = "none";
                };
                document.getElementById("swal-mejorar-descartar").onclick = () => {
                  document.getElementById("swal-mejorar-preview").style.display = "none";
                };
              }
            } catch {
              document.getElementById("swal-mejorar-texto").textContent = "Error al conectar con la IA.";
              document.getElementById("swal-mejorar-preview").style.display = "";
            } finally {
              btn.disabled = false;
              btn.innerHTML = '<i class="fas fa-wand-magic-sparkles me-1"></i>Mejorar redacción con IA';
            }
          });
        };

        // Leer .txt / .md / .html / .docx y volcar en el editor (tipo texto)
        const volcarArchivoEnEditor = async(file) => {
          if (!file || !quill) return;
          const fname = document.getElementById("swal-txt-fname");
          fname.textContent = file.name;
          if (!esArchivoDeContenido(file.name)) {
            fname.textContent = `${file.name} — formato no admitido para el contenido`;
            return;
          }
          const ext = fileExtension(file.name);
          try {
            if (ext === "md" || ext === "markdown") {
              const text = await file.text();
              quill.clipboard.dangerouslyPasteHTML(marked.parse(text));
            } else if (ext === "txt") {
              const text = await file.text();
              quill.setText(text);
            } else if (ext === "html" || ext === "htm") {
              const html = await file.text();
              quill.clipboard.dangerouslyPasteHTML(html);
            } else if (ext === "docx") {
              const buffer = await file.arrayBuffer();
              const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
              quill.clipboard.dangerouslyPasteHTML(result.value);
            }
          } catch {
            fname.textContent = `${file.name} — error al leer el archivo`;
          }
        };
        document.getElementById("swal-txt-file").addEventListener("change", (e) => {
          volcarArchivoEnEditor(e.target.files[0]);
          e.target.value = "";
        });
        const txtDrop = document.getElementById("swal-txt-drop");
        txtDrop.addEventListener("dragover", (e) => { e.preventDefault(); txtDrop.classList.add("is-over"); });
        txtDrop.addEventListener("dragleave", () => txtDrop.classList.remove("is-over"));
        txtDrop.addEventListener("drop", (e) => {
          e.preventDefault();
          txtDrop.classList.remove("is-over");
          volcarArchivoEnEditor(e.dataTransfer?.files?.[0]);
        });

        // Guardar deshabilitado mientras haya archivos subiendo o procesándose
        const updateConfirm = () => {
          const btn = Swal.getConfirmButton();
          if (btn) btn.disabled = principalUploading || adicionales.some(i => i.status === "uploading");
        };

        const renderPrincipalActual = () => {
          const el = document.getElementById("swal-principal-actual");
          if (!el) return;
          el.innerHTML = esArchivoPropio(principal)
            ? `<small class="text-muted mt-1 d-block"><i class="fas fa-paperclip me-1"></i>Archivo actual: ${escapeHtml(principal.nombre)}</small>`
            : "";
        };

        // Upload del archivo principal (tipos no-texto) con extracción automática del texto
        document.getElementById("swal-file")?.addEventListener("change", async(e) => {
          const file = e.target.files[0];
          if (!file) return;
          const statusEl = document.getElementById("swal-upload-status");
          const transGroup = document.getElementById("swal-transcripcion-group");
          statusEl.style.cssText = "display:block!important";
          statusEl.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>Subiendo y procesando...';
          transGroup.style.display = "none";
          principalUploading = true;
          updateConfirm();
          try {
            const adj = await subirArchivo(file);
            principal = adj;
            principalTexto = adj.texto || "";
            document.getElementById("swal-transcripcion").value = principalTexto;
            transGroup.style.display = "";
            statusEl.innerHTML = adj.error
              ? `<i class="fas fa-exclamation-triangle text-warning me-1"></i>${escapeHtml(file.name)}: no se pudo extraer el texto. Podés escribirlo a mano.`
              : `<i class="fas fa-check text-success me-1"></i>${escapeHtml(file.name)}${adj.tieneTexto ? " — texto extraído" : ""}`;
            renderPrincipalActual();
          } catch (err) {
            statusEl.innerHTML = `<i class="fas fa-exclamation-triangle text-danger me-1"></i>Error: ${escapeHtml(err.message)}`;
          } finally {
            principalUploading = false;
            updateConfirm();
          }
        });

        // Transcripción de YouTube — fetch directo desde el browser (evita bloqueo de IPs de CF Workers)
        document.getElementById("swal-yt-transcript-btn")?.addEventListener("click", async() => {
          const rawUrl = document.getElementById("swal-url")?.value?.trim();
          const videoId = extractYoutubeId(rawUrl);
          if (!videoId) {
            // Aviso dentro del modal: un Swal.fire (aunque sea toast) cerraría el formulario
            Swal.showValidationMessage("No se encontró el ID del video");
            return;
          }
          Swal.resetValidationMessage();
          const btn = document.getElementById("swal-yt-transcript-btn");
          btn.disabled = true;
          btn.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>Obteniendo transcripción...';
          try {
            const resp = await ApiService.get(`admin/yt-transcript?videoId=${encodeURIComponent(videoId)}`);
            if (resp?.transcripcion) {
              document.getElementById("swal-transcripcion").value = resp.transcripcion;
              document.getElementById("swal-transcripcion-group").style.display = "";
            } else {
              const ta = document.getElementById("swal-transcripcion");
              ta.value = "";
              ta.placeholder = "No se encontraron subtítulos automáticos. Podés escribirlos manualmente.";
              document.getElementById("swal-transcripcion-group").style.display = "";
            }
          } catch {
            const ta = document.getElementById("swal-transcripcion");
            ta.value = "";
            ta.placeholder = "Error al obtener subtítulos. Podés escribirlos manualmente.";
            document.getElementById("swal-transcripcion-group").style.display = "";
          } finally {
            btn.disabled = false;
            btn.innerHTML = '<i class="fas fa-closed-captioning me-1"></i>Obtener transcripción YouTube';
          }
        });

        // Cambiar entre URL / Subir archivo
        const toggleModo = () => {
          const upload = document.getElementById("swal-modo-upload")?.checked;
          document.getElementById("swal-url-wrap").style.display = upload ? "none" : "";
          document.getElementById("swal-file-wrap").style.display = upload ? "" : "none";
          // Con URL el texto se escribe a mano desde el principio (el Worker no lee la URL);
          // al subir, el campo aparece cuando hay archivo y trae el texto extraído
          if (tipoSelect.value !== "texto") {
            const transGroup = document.getElementById("swal-transcripcion-group");
            if (!upload) transGroup.style.display = "";
            else if (!principal) transGroup.style.display = "none";
          }
        };
        document.getElementById("swal-modo-url")?.addEventListener("change", toggleModo);
        document.getElementById("swal-modo-upload")?.addEventListener("change", toggleModo);

        // Toggle principal por tipo
        const toggle = () => {
          const tipo = tipoSelect.value;
          const isText = tipo === "texto";
          const isAudio = tipo === "audio";
          document.getElementById("swal-contenido-group").style.display = isText ? "" : "none";
          document.getElementById("swal-recurso-group").style.display = isText ? "none" : "";
          // El editor se crea una sola vez y se conserva al cambiar de tipo: recrearlo sobre
          // el mismo contenedor duplicaba los listeners (emojis, "Mejorar redacción")
          if (isText) initEditor();

          // Audio: solo modo upload (sin opción URL)
          const modoUrlLabel = document.getElementById("swal-modo-url")?.closest(".form-check");
          if (isAudio) {
            document.getElementById("swal-modo-upload").checked = true;
            if (modoUrlLabel) modoUrlLabel.style.display = "none";
          } else {
            if (modoUrlLabel) modoUrlLabel.style.display = "";
          }

          const fileInput = document.getElementById("swal-file");
          if (fileInput) {
            const accepts = {
              pdf: ".pdf",
              imagen: ".jpg,.jpeg,.png,.gif,.webp",
              video: ".mp4,.webm",
              audio: ".mp3,.wav,.m4a,.ogg,.flac,.webm",
            };
            fileInput.accept = accepts[tipo] || "";
          }

          // Botón de transcripción YouTube: solo para tipo video en modo URL
          const ytBtn = document.getElementById("swal-yt-transcript-btn");
          if (ytBtn) ytBtn.style.display = tipo === "video" ? "" : "none";

          // Con archivo principal: mostrar su texto (editable) para la búsqueda
          const transGroup = document.getElementById("swal-transcripcion-group");
          if (!isText && principal) {
            document.getElementById("swal-transcripcion").value = principalTexto ?? "";
            transGroup.style.display = "";
          } else if (!isText) {
            document.getElementById("swal-transcripcion").value = "";
            transGroup.style.display = "none";
          }

          toggleModo();
        };
        tipoSelect.addEventListener("change", toggle);
        toggle();
        renderPrincipalActual();

        // ── Archivos adicionales ─────────────────────────────────────────────
        const adjList = document.getElementById("swal-adj-list");

        const renderAdjuntos = () => {
          adjList.innerHTML = adicionales.map(it => {
            const nombre = it.adj?.nombre || it.file?.name || "";
            const tamano = formatSize(it.adj?.tamano ?? it.file?.size);
            const est = estadoAdjunto(it);
            const ok = it.status === "ok";
            const puedeReintentar = (it.status === "error" && it.file) || (ok && it.adj?.error && esArchivoPropio(it.adj));
            const puedePrincipal = ok && tipoForFile(nombre) && esArchivoPropio(it.adj);
            const puedeContenido = ok && esArchivoDeContenido(nombre);
            const btn = (action, icon, title, cls = "btn-outline-secondary") =>
              `<button type="button" class="btn ${cls} btn-sm" data-action="${action}" data-key="${it.key}" title="${title}"><i class="fas ${icon}"></i></button>`;
            const editor = it.abierto
              ? (it.cargando
                  ? '<div class="small text-muted mt-2"><i class="fas fa-circle-notch fa-spin me-1"></i>Cargando texto...</div>'
                  : `<textarea class="form-control form-control-sm mt-2" rows="4" data-key="${it.key}"
                    placeholder="Sin texto. Podés escribir uno para que el archivo aparezca en la búsqueda.">${escapeHtml(it.texto ?? "")}</textarea>`)
              : "";
            return `
              <div class="kb-adj-row border rounded p-2 mb-2">
                <div class="d-flex align-items-center gap-2">
                  <i class="fas ${iconForFile(nombre)} text-muted fa-lg"></i>
                  <div class="flex-grow-1 min-w-0">
                    <div class="small fw-semibold text-truncate">${escapeHtml(nombre)}</div>
                    <small class="${est.cls}"><i class="fas ${est.icon} me-1"></i>${escapeHtml(est.label)}</small>
                    ${tamano ? `<small class="text-muted ms-2">${tamano}</small>` : ""}
                  </div>
                  <div class="d-flex gap-1 flex-shrink-0">
                    ${ok ? btn("texto", "fa-file-lines", "Ver / editar texto") : ""}
                    ${puedePrincipal ? btn("principal", "fa-star", "Usar como archivo principal") : ""}
                    ${puedeContenido ? btn("contenido", "fa-file-import", "Usar como contenido del artículo") : ""}
                    ${puedeReintentar ? btn("reintentar", "fa-rotate-right", "Reintentar") : ""}
                    ${it.status !== "uploading" ? btn("quitar", "fa-xmark", "Quitar", "btn-outline-danger") : ""}
                  </div>
                </div>
                ${editor}
              </div>`;
          }).join("");
          updateConfirm();
        };

        const uploadItem = async(it) => {
          it.status = "uploading";
          renderAdjuntos();
          try {
            it.adj = await subirArchivo(it.file);
            it.texto = it.adj.texto ?? null;
            it.textoOriginal = it.texto;
            it.status = "ok";
          } catch (err) {
            it.status = "error";
            it.errorMsg = err.message;
          }
          renderAdjuntos();
        };

        const addFiles = (fileList) => {
          const nuevos = [...fileList].map(file => ({ key: `tmp${++tmpKey}`, file, status: "uploading" }));
          adicionales.push(...nuevos);
          nuevos.forEach(uploadItem); // en paralelo
        };

        const actions = {
          texto: async(it) => {
            it.abierto = !it.abierto;
            if (it.abierto && it.texto === undefined) {
              it.cargando = true;
              renderAdjuntos();
              it.texto = it.adj.tieneTexto ? await cargarTextoAdjunto(it.adj.id) : "";
              it.textoOriginal = it.texto;
              it.cargando = false;
            }
            renderAdjuntos();
          },
          principal: async(it) => {
            const anterior = principal;
            principal = it.adj;
            principalTexto = it.texto ?? (it.adj.tieneTexto ? await cargarTextoAdjunto(it.adj.id) : "");
            adicionales = adicionales.filter(x => x !== it);
            // El principal anterior pasa a adicionales (los enlaces externos se descartan)
            if (esArchivoPropio(anterior)) adicionales.unshift({ key: anterior.id, status: "ok", adj: anterior });
            tipoSelect.value = tipoForFile(it.adj.nombre);
            document.getElementById("swal-modo-upload").checked = true;
            toggle();
            renderPrincipalActual();
            renderAdjuntos();
          },
          // Documento de texto (md, txt, html, docx): su contenido pasa al editor y deja de ser adjunto
          contenido: async(it) => {
            if (quill?.getText().trim() && !confirm("¿Reemplazar el contenido actual del editor?")) return;
            if (tipoSelect.value !== "texto") {
              // El archivo principal anterior queda como adicional
              if (esArchivoPropio(principal)) adicionales.unshift({ key: principal.id, status: "ok", adj: principal });
              principal = null;
              principalTexto = null;
              tipoSelect.value = "texto";
              toggle();
              renderPrincipalActual();
            }
            if (it.file) {
              await volcarArchivoEnEditor(it.file);
            } else {
              const texto = it.texto ?? (it.adj.tieneTexto ? await cargarTextoAdjunto(it.adj.id) : "");
              const ext = fileExtension(it.adj.nombre);
              if (ext === "md" || ext === "markdown") quill.clipboard.dangerouslyPasteHTML(marked.parse(texto));
              else quill.setText(texto);
              document.getElementById("swal-txt-fname").textContent = it.adj.nombre;
            }
            adicionales = adicionales.filter(x => x !== it);
            renderAdjuntos();
            document.getElementById("swal-contenido-group").scrollIntoView({ behavior: "smooth", block: "start" });
          },
          reintentar: async(it) => {
            if (it.status === "error") return uploadItem(it);
            it.status = "uploading";
            renderAdjuntos();
            try {
              const resp = await ApiService.post("admin/adjunto/reprocesar", { id: it.adj.id });
              it.adj = resp.data;
              it.texto = resp.data.texto ?? null;
              it.textoOriginal = it.texto;
            } catch (err) {
              it.adj = { ...it.adj, error: err.message };
            }
            it.status = "ok";
            renderAdjuntos();
          },
          quitar: (it) => {
            adicionales = adicionales.filter(x => x !== it);
            renderAdjuntos();
          },
        };

        adjList.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-action]");
          const it = btn && adicionales.find(x => x.key === btn.dataset.key);
          if (it) actions[btn.dataset.action]?.(it);
        });
        adjList.addEventListener("input", (e) => {
          const it = adicionales.find(x => x.key === e.target.dataset.key);
          if (it && e.target.tagName === "TEXTAREA") it.texto = e.target.value;
        });

        document.getElementById("swal-adj-input").addEventListener("change", (e) => {
          addFiles(e.target.files);
          e.target.value = "";
        });
        const drop = document.getElementById("swal-adj-drop");
        drop.addEventListener("dragover", (e) => { e.preventDefault(); drop.classList.add("is-over"); });
        drop.addEventListener("dragleave", () => drop.classList.remove("is-over"));
        drop.addEventListener("drop", (e) => {
          e.preventDefault();
          drop.classList.remove("is-over");
          if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
        });

        renderAdjuntos();
      },
      willClose: () => {
        quill = null;
      },
      showCancelButton: true,
      confirmButtonText: art ? "Guardar cambios" : "Crear artículo",
      cancelButtonText: "Cancelar",
      returnFocus: false,
      preConfirm: async() => {
        if (principalUploading || adicionales.some(i => i.status === "uploading")) {
          Swal.showValidationMessage("Esperá a que terminen de procesarse los archivos");
          return false;
        }
        if (!document.getElementById("swal-titulo").value.trim()) {
          Swal.showValidationMessage("El título es requerido");
          return false;
        }

        const tipo = document.getElementById("swal-tipo").value;
        const modoUpload = document.getElementById("swal-modo-upload")?.checked;
        const rawHtml = quill ? quill.root.innerHTML : "";
        const textoRag = document.getElementById("swal-transcripcion")?.value?.trim() || "";
        const extra = [];
        let principalFinal = null;

        try {
          if (tipo !== "texto") {
            if (modoUpload) {
              if (!esArchivoPropio(principal)) {
                Swal.showValidationMessage("Subí el archivo del artículo");
                return false;
              }
              principalFinal = principal;
            } else {
              let url = document.getElementById("swal-url")?.value?.trim() || "";
              if (!url) {
                Swal.showValidationMessage("Ingresá la URL del recurso");
                return false;
              }
              // Un ID de YouTube suelto se guarda como URL completa
              if (!/^https?:\/\//i.test(url) && extractYoutubeId(url)) url = `https://www.youtube.com/watch?v=${extractYoutubeId(url)}`;
              if (principal && principal.url === url) {
                principalFinal = principal;
              } else {
                const resp = await ApiService.post("admin/adjunto/enlace", {
                  url, nombre: url, texto: textoRag, origen: isYoutubeUrl(url) ? "youtube" : "manual",
                });
                principalFinal = resp.data;
                principalTexto = textoRag;
              }
            }
            // Texto del principal corregido a mano
            if (principalFinal === principal && textoRag !== (principalTexto || "")) {
              await ApiService.post("admin/adjunto/editar", { id: principal.id, texto: textoRag });
            }
          } else if (esArchivoPropio(principal)) {
            // Pasar a tipo texto no borra el archivo: queda como adicional
            extra.push({ status: "ok", adj: principal });
          }

          for (const it of adicionales) {
            if (it.status === "ok" && it.texto !== undefined && (it.texto ?? "") !== (it.textoOriginal ?? "")) {
              await ApiService.post("admin/adjunto/editar", { id: it.adj.id, texto: it.texto });
            }
          }
        } catch (err) {
          Swal.showValidationMessage(`Error al guardar los archivos: ${err.message}`);
          return false;
        }

        return {
          titulo: document.getElementById("swal-titulo").value,
          descripcion: document.getElementById("swal-desc").value,
          categoriaId: document.getElementById("swal-cat").value,
          // Con el flag apagado no se envía: el Worker la limpia solo si cambia la categoría
          ...(AppConfig.features.subcategories
            ? { subcategoriaId: document.getElementById("swal-subcat").value || null }
            : {}),
          tipo,
          autor: document.getElementById("swal-autor").value,
          // contenido es solo el cuerpo escrito; el texto de los archivos vive en sus adjuntos
          contenido: tipo === "texto" && rawHtml !== "<p><br></p>" ? rawHtml : null,
          ...buildAdjuntosPayload(principalFinal, [...adicionales, ...extra]),
          tags: JSON.parse(document.getElementById("swal-tags-value").value || "[]"),
          fecha: new Date().toLocaleDateString("es-AR"),
        };
      },
    });

    if (!formValues) return;

    const endpoint = art ? "admin/articulo/editar" : "admin/articulo/crear";
    const payload = art ? { ...formValues, id: art.id } : formValues;
    const saveResp = await ApiService.post(endpoint, payload);
    const savedArt = saveResp?.data || { ...formValues, id: art?.id };

    // Popup de relacionados (post-guardado)
    const allArtsResp = await ApiService.post("admin/articulos");
    const allArts = allArtsResp?.data || [];
    const catMap = Object.fromEntries(categorias.map(c => [c.id, c]));

    // La relación es de ida y vuelta: se guarda de este lado y, si se desmarca una que
    // venía de otro artículo, se quita también de la lista de ese artículo.
    const artRel = allArts.find(a => a.id === savedArt.id) || savedArt;
    const nuevosRelacionados = await AdminController._showRelacionadosForm(artRel, allArts, catMap);
    if (nuevosRelacionados !== null) {
      const { propios, quitarDe } = planRelacionados(artRel, allArts, nuevosRelacionados);
      await ApiService.post("admin/articulo/editar", {
        id: savedArt.id,
        relacionadosIds: propios.length ? propios : null,
      });
      for (const otro of quitarDe) {
        await ApiService.post("admin/articulo/editar", otro);
      }
    }

    Swal.fire({ icon: "success", title: art ? "Artículo actualizado" : "Artículo creado", toast: true, position: "top-end", timer: 2000, showConfirmButton: false });
    Swal.fire({
      icon: "info",
      title: "Indexando en la base de conocimiento",
      text: "El contenido se está procesando para el chat IA. Si acabás de crear o editar un artículo extenso, esperá unos segundos antes de consultarlo.",
      toast: true,
      position: "bottom-end",
      timer: 6000,
      showConfirmButton: false,
      timerProgressBar: true,
    });
    AdminController.render();
  },

  _showRelacionadosForm: async(art, allArts, catMap) => {
    // Preseleccionados en los dos sentidos: los propios y los artículos que lo tienen en su lista
    const selectedIds = new Set(relacionadosDe(art, allArts));
    const inversos = new Set(relacionadosInversos(art.id, allArts));
    let includeOtherCats = false;
    let searchQuery = "";

    const renderList = (container) => {
      const q = searchQuery.toLowerCase();
      const candidates = allArts.filter(a => {
        if (a.id === art.id) return false;
        if (!includeOtherCats && a.categoriaId !== art.categoriaId) return false;
        if (q) return a.titulo.toLowerCase().includes(q) || (a.descripcion || "").toLowerCase().includes(q);
        return true;
      });

      if (candidates.length === 0) {
        container.innerHTML = `<p class="text-muted text-center small py-3 mb-0">Sin resultados.</p>`;
        return;
      }

      container.innerHTML = candidates.map(a => `
        <label class="d-flex align-items-center gap-2 px-2 py-2 rounded" style="cursor:pointer">
          <input type="checkbox" class="form-check-input flex-shrink-0 rel-check" value="${a.id}"
            ${selectedIds.has(a.id) ? "checked" : ""}>
          <div class="flex-grow-1 min-w-0">
            <div class="small fw-semibold text-truncate">${a.titulo}</div>
            <small class="text-muted">${catMap[a.categoriaId]?.nombre || "—"}${inversos.has(a.id) ? " · relacionado desde ese artículo" : ""}</small>
          </div>
          <span class="badge kb-tipo-badge kb-tipo-${a.tipo} flex-shrink-0">${TIPO_LABELS[a.tipo] || a.tipo}</span>
        </label>
      `).join("");

      container.querySelectorAll(".rel-check").forEach(cb => {
        cb.addEventListener("change", () => {
          if (cb.checked) selectedIds.add(cb.value);
          else selectedIds.delete(cb.value);
        });
      });
    };

    const { isConfirmed } = await Swal.fire({
      title: "Artículos relacionados",
      width: 640,
      html: `
        <div class="text-start">
          <p class="text-muted small mb-2">Artículo: <strong>${art.titulo}</strong></p>
          <p class="text-muted small mb-2">La relación se ve en los dos artículos: no hace falta marcarla del otro lado.</p>
          <div class="d-flex gap-2 mb-2">
            <input id="rel-search" class="form-control form-control-sm flex-grow-1"
              placeholder="Buscar por título o descripción…">
            <button type="button" id="rel-ia-btn" class="btn btn-outline-primary btn-sm text-nowrap">
              <i class="fas fa-robot me-1"></i>Sugerir con IA
            </button>
          </div>
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="form-check mb-0">
              <input class="form-check-input" type="checkbox" id="rel-otras-cats">
              <label class="form-check-label small" for="rel-otras-cats">Incluir otras categorías</label>
            </div>
            <small id="rel-status" class="fst-italic"></small>
          </div>
          <div id="rel-list"
            style="max-height:230px;overflow-y:auto;border:1px solid #dee2e6;border-radius:6px;padding:4px">
          </div>
        </div>
      `,
      didOpen: () => {
        const container = document.getElementById("rel-list");
        renderList(container);

        document.getElementById("rel-search").addEventListener("input", e => {
          searchQuery = e.target.value;
          renderList(container);
        });

        document.getElementById("rel-otras-cats").addEventListener("change", e => {
          includeOtherCats = e.target.checked;
          renderList(container);
        });

        document.getElementById("rel-ia-btn").addEventListener("click", async() => {
          const btn = document.getElementById("rel-ia-btn");
          const status = document.getElementById("rel-status");
          btn.disabled = true;
          btn.innerHTML = `<i class="fas fa-circle-notch fa-spin me-1"></i>Consultando…`;
          status.textContent = "";

          try {
            const resp = await ApiService.post("admin/articulo/sugerirRelacionados", {
              id: art.id,
              titulo: art.titulo,
              descripcion: art.descripcion,
              contenido: art.contenido,
              categoriaId: art.categoriaId,
              includeOtherCats,
            });
            const sugeridos = resp?.data || [];
            sugeridos.forEach(id => selectedIds.add(id));
            renderList(container);
            status.textContent = sugeridos.length
              ? `IA sugirió ${sugeridos.length} artículo${sugeridos.length > 1 ? "s" : ""}`
              : "Sin sugerencias adicionales";
            status.className = sugeridos.length ? "text-success small fst-italic" : "text-muted small fst-italic";
          } catch {
            status.textContent = "Error al consultar la IA";
            status.className = "text-danger small fst-italic";
          } finally {
            btn.disabled = false;
            btn.innerHTML = `<i class="fas fa-robot me-1"></i>Sugerir con IA`;
          }
        });
      },
      showCancelButton: true,
      confirmButtonText: "Guardar relacionados",
      cancelButtonText: "Omitir",
      preConfirm: () => [...selectedIds],
    });

    return isConfirmed ? [...selectedIds] : null;
  },

  // Editor de subcategorías dentro del Swal de categoría: alta, renombrar, ordenar y borrar.
  // getSubs/setSubs comparten el estado con _showCatForm.
  _bindSubcatsEditor: (categoriaId, getSubs, setSubs) => {
    const list = document.getElementById("sc-subs-list");
    const input = document.getElementById("sc-sub-nueva");
    const status = document.getElementById("sc-subs-status");
    let confirmarBorrado = null; // id pendiente de confirmar (segundo clic)

    const setStatus = (msg, cls = "text-muted") => { status.className = `d-block mt-1 ${cls}`; status.textContent = msg; };

    const render = () => {
      const subs = getSubs();
      list.innerHTML = subs.length
        ? subs.map((s, i) => `
        <div class="input-group input-group-sm mb-1">
          <input class="form-control" data-sub-nombre="${escapeHtml(s.id)}" value="${escapeHtml(s.nombre)}">
          <button type="button" class="btn btn-outline-secondary" data-sub-mover="-1" data-i="${i}" title="Subir" ${i === 0 ? "disabled" : ""}><i class="fas fa-arrow-up"></i></button>
          <button type="button" class="btn btn-outline-secondary" data-sub-mover="1" data-i="${i}" title="Bajar" ${i === subs.length - 1 ? "disabled" : ""}><i class="fas fa-arrow-down"></i></button>
          <button type="button" class="btn ${confirmarBorrado === s.id ? "btn-danger" : "btn-outline-danger"}" data-sub-borrar="${escapeHtml(s.id)}" title="Borrar">
            ${confirmarBorrado === s.id ? "¿Borrar?" : '<i class="fas fa-trash"></i>'}
          </button>
        </div>`).join("")
        : '<div class="small text-muted">Sin subcategorías.</div>';
    };

    const run = async(fn, okMsg) => {
      try {
        await fn();
        setStatus(okMsg, "text-success");
      } catch (err) {
        setStatus(`Error: ${err.message}`, "text-danger");
      }
      render();
    };

    const agregar = () => {
      const nombre = input.value.trim();
      if (!nombre) return;
      run(async() => {
        const resp = await ApiService.post("admin/subcategoria/crear", { categoriaId, nombre });
        setSubs([...getSubs(), resp.data]);
        input.value = "";
      }, `"${nombre}" agregada`);
    };
    document.getElementById("sc-sub-agregar").addEventListener("click", agregar);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); agregar(); } });

    // Renombrar al salir del campo (o con Enter)
    list.addEventListener("change", (e) => {
      const id = e.target.dataset.subNombre;
      const nombre = e.target.value.trim();
      const sub = getSubs().find(s => s.id === id);
      if (!sub || !nombre || nombre === sub.nombre) { if (sub) e.target.value = sub.nombre; return; }
      run(async() => {
        await ApiService.post("admin/subcategoria/editar", { id, nombre });
        setSubs(getSubs().map(s => s.id === id ? { ...s, nombre } : s));
      }, "Nombre actualizado");
    });
    list.addEventListener("keydown", (e) => { if (e.key === "Enter" && e.target.dataset.subNombre) { e.preventDefault(); e.target.blur(); } });

    list.addEventListener("click", (e) => {
      const mover = e.target.closest("[data-sub-mover]");
      if (mover) {
        const nueva = moverItem(getSubs(), Number(mover.dataset.i), Number(mover.dataset.subMover));
        run(async() => {
          // Se reescribe el orden de las que cambiaron de posición
          const cambios = nueva.filter((s, i) => s.orden !== i);
          await Promise.all(cambios.map(s => ApiService.post("admin/subcategoria/editar", { id: s.id, orden: nueva.indexOf(s) })));
          setSubs(nueva.map((s, i) => ({ ...s, orden: i })));
        }, "Orden actualizado");
        return;
      }
      const borrar = e.target.closest("[data-sub-borrar]");
      if (borrar) {
        const id = borrar.dataset.subBorrar;
        if (confirmarBorrado !== id) {
          confirmarBorrado = id;
          setStatus("Clic de nuevo para confirmar. Sus artículos quedan sin subcategoría.", "text-warning");
          render();
          return;
        }
        confirmarBorrado = null;
        run(async() => {
          await ApiService.post("admin/subcategoria/eliminar", { id });
          setSubs(getSubs().filter(s => s.id !== id));
        }, "Subcategoría borrada");
      }
    });

    render();
  },

  _bindCatActions: () => {
    document.getElementById("btn-new-categoria")?.addEventListener("click", () => {
      AdminController._showCatForm(null);
    });
    document.querySelectorAll(".btn-edit-cat").forEach(btn => {
      btn.addEventListener("click", async() => {
        if (!AuthService.isEditor()) return AdminController._showCatForm({ id: btn.dataset.id });
        const resp = await ApiService.post("admin/categorias");
        const cat = (resp?.data || []).find(c => c.id === btn.dataset.id);
        if (cat) AdminController._showSubcatsForm(cat);
      });
    });
  },

  _showCatForm: async(cat) => {
    // Si solo tenemos el id (desde el botón editar), cargamos los datos completos
    if (cat?.id && !cat.nombre) {
      const resp = await ApiService.post("admin/categorias");
      cat = (resp?.data || []).find(c => c.id === cat.id) || cat;
    }

    const iconosOpts = ICONOS_CAT.map(i =>
      `<option value="${i}" ${cat?.icono === i ? "selected" : ""}>${i.replace("fa-", "")}</option>`
    ).join("");
    const colorOpts = COLORES.map(c =>
      `<option value="${c}" ${cat?.color === c ? "selected" : ""}>${c}</option>`
    ).join("");

    const apiEnabled = AppConfig.features.apiConnector;
    const isApiCat = cat?.tipo === 'api';
    const apiConfigVal = cat?.api_config || '';

    // Plantilla JSON de ejemplo para conectores API
    const API_CONFIG_TEMPLATE = JSON.stringify({
      version: 1,
      request: {
        base_url: "https://api.ejemplo.com",
        path: "/v1/recurso/{campo1}",
        method: "GET",
        auth: { type: "header", secret_name: "MI_API_KEY", header_name: "X-Api-Key" },
        query_params: {},
        body_type: "none",
        timeout_ms: 10000
      },
      inputs: [
        {
          key: "campo1",
          label: "Identificador",
          type: "text",
          placeholder: "Ej: ABC123",
          required: true,
          placement: "path",
          validation: { min_length: 3, max_length: 20, message: "Ingresá un identificador válido" }
        }
      ],
      response: {
        data_path: "",
        fields: [
          { key: "campo_a", label: "Campo A", path: "campo_a" },
          { key: "campo_b", label: "Campo B", path: "nested.campo_b" }
        ],
        images_path: null,
        not_found_path: null,
        not_found_value: null
      },
      ai: {
        system_prompt: "Sos un asistente especializado. Respondé siempre en español.",
        user_prompt_template: "Datos obtenidos para {{campo1}}:\n{{datos_json}}",
        max_tokens: 400,
        few_shot: []
      },
      cache: { ttl_minutes: 0, key_inputs: ["campo1"] },
      ui: {
        submit_label: "Consultar",
        submit_icon: "fa-magnifying-glass",
        result_title_field: "campo_a",
        show_raw_fields: true
      }
    }, null, 2);

    const tipoSection = apiEnabled
      ? `
      <div class="mb-3 border-top pt-3 mt-3">
        <label class="form-label fw-semibold">Tipo de categoría</label>
        <select id="sc-tipo" class="form-select">
          <option value="standard" ${!isApiCat ? "selected" : ""}>Estándar (artículos)</option>
          <option value="api"      ${isApiCat ? "selected" : ""}>Conector API externo</option>
        </select>
      </div>
      <div id="sc-api-section" style="display:${isApiCat ? 'block' : 'none'}">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label fw-semibold mb-0">Configuración del conector (JSON)</label>
          <button type="button" id="sc-api-template-btn" class="btn btn-outline-secondary btn-sm">
            <i class="fas fa-file-code me-1"></i>Usar plantilla
          </button>
        </div>
        <textarea id="sc-api-config" class="form-control font-monospace"
          rows="10" style="font-size:0.75rem"
          placeholder='{ "version": 1, "request": { ... } }'>${apiConfigVal}</textarea>
        <div id="sc-api-json-error" class="text-danger small mt-1" style="display:none"></div>
      </div>
    `
      : '';

    // Subcategorías: se gestionan con la categoría ya creada y cada cambio se guarda al momento
    const subcatsEnabled = AppConfig.features.subcategories;
    let subs = [...(cat?.subcategorias || [])];
    let subsCambiaron = false;
    const subsSection = !subcatsEnabled
      ? ""
      : `
      <div class="mb-3 border-top pt-3 mt-3" id="sc-subs-section" style="display:${isApiCat ? "none" : "block"}">
        <label class="form-label fw-semibold mb-1">Subcategorías</label>
        ${cat?.id
          ? `<div class="form-text mt-0 mb-2">Los cambios en subcategorías se guardan al momento.</div>
             <div id="sc-subs-list"></div>
             <div class="input-group input-group-sm mt-2">
               <input id="sc-sub-nueva" class="form-control" placeholder="Nueva subcategoría (ej. Procedimientos)">
               <button type="button" id="sc-sub-agregar" class="btn btn-outline-primary"><i class="fas fa-plus me-1"></i>Agregar</button>
             </div>
             <small id="sc-subs-status" class="d-block mt-1"></small>`
          : '<div class="form-text mt-0">Guardá la categoría para agregarle subcategorías.</div>'}
      </div>`;

    const { value } = await Swal.fire({
      title: cat?.id ? "Editar categoría" : "Nueva categoría",
      html: `
        <div class="text-start">
          <div class="mb-3">
            <label class="form-label fw-semibold">Nombre</label>
            <input id="sc-nombre" class="form-control" value="${cat?.nombre || ""}">
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Descripción</label>
            <input id="sc-desc" class="form-control" value="${cat?.descripcion || ""}">
          </div>
          <div class="row mb-3">
            <div class="col">
              <label class="form-label fw-semibold">Ícono (Font Awesome)</label>
              <select id="sc-icono" class="form-select">${iconosOpts}</select>
            </div>
            <div class="col">
              <label class="form-label fw-semibold">Color</label>
              <select id="sc-color" class="form-select">${colorOpts}</select>
            </div>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="sc-publica" ${cat?.es_publica ? "checked" : ""}>
            <label class="form-check-label" for="sc-publica">
              Categoría pública <span class="text-muted small">(visible para todos los usuarios)</span>
            </label>
          </div>
          ${tipoSection}
          ${subsSection}
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      width: apiEnabled ? '640px' : undefined,
      didOpen: () => {
        if (subcatsEnabled && cat?.id) AdminController._bindSubcatsEditor(cat.id, () => subs, (v) => { subs = v; subsCambiaron = true; });
        if (!apiEnabled) return;
        const tipoSel = document.getElementById('sc-tipo');
        const apiSection = document.getElementById('sc-api-section');
        const templateBtn = document.getElementById('sc-api-template-btn');
        const subsSect = document.getElementById('sc-subs-section');
        tipoSel?.addEventListener('change', () => {
          apiSection.style.display = tipoSel.value === 'api' ? 'block' : 'none';
          if (subsSect) subsSect.style.display = tipoSel.value === 'api' ? 'none' : 'block';
        });
        templateBtn?.addEventListener('click', () => {
          document.getElementById('sc-api-config').value = API_CONFIG_TEMPLATE;
          document.getElementById('sc-api-json-error').style.display = 'none';
        });
      },
      preConfirm: () => {
        const nombre = document.getElementById("sc-nombre").value.trim();
        const descripcion = document.getElementById("sc-desc").value;
        const icono = document.getElementById("sc-icono").value;
        const color = document.getElementById("sc-color").value;
        const esPublica = document.getElementById("sc-publica").checked;

        if (!nombre) {
          Swal.showValidationMessage("El nombre es requerido");
          return false;
        }

        let tipo = 'standard';
        let apiConfig = null;
        if (apiEnabled) {
          tipo = document.getElementById('sc-tipo')?.value || 'standard';
          if (tipo === 'api') {
            const raw = (document.getElementById('sc-api-config')?.value || '').trim();
            if (!raw) {
              Swal.showValidationMessage("La configuración JSON es requerida para conectores API");
              return false;
            }
            try {
              JSON.parse(raw);
              apiConfig = raw;
            } catch (e) {
              const errEl = document.getElementById('sc-api-json-error');
              if (errEl) { errEl.textContent = `JSON inválido: ${e.message}`; errEl.style.display = ''; }
              Swal.showValidationMessage("La configuración JSON no es válida");
              return false;
            }
          }
        }

        return { nombre, descripcion, icono, color, esPublica, tipo, apiConfig };
      },
    });

    if (!value) {
      // Cancelar no deshace las subcategorías (ya se guardaron): refrescar la lista
      if (subsCambiaron) AdminController.render();
      return;
    }
    const endpoint = cat?.id ? "admin/categoria/editar" : "admin/categoria/crear";
    const payload = cat?.id ? { ...value, id: cat.id } : value;
    await ApiService.post(endpoint, payload);
    Swal.fire({ icon: "success", title: "Categoría guardada", timer: 2000, showConfirmButton: false });
    AdminController.render();
  },

  _bindUserActions: (categorias) => {
    // Con un proveedor de ingreso externo los usuarios se crean solos al entrar
    if (AuthService.isExterno()) {
      const aviso = document.createElement("span");
      aviso.className = "text-muted small";
      aviso.id = "users-externo-hint";
      aviso.textContent = `Los usuarios se crean al ingresar con ${AppConfig.auth.label}`;
      document.getElementById("btn-new-user")?.replaceWith(aviso);
    }

    // Nuevo usuario
    document.getElementById("btn-new-user")?.addEventListener("click", () => {
      AdminController._showUserForm(null, categorias);
    });

    // Editar usuario (datos + permisos + estado en un solo form)
    document.querySelectorAll(".btn-user-edit").forEach(btn => {
      btn.addEventListener("click", async() => {
        const usersResp = await ApiService.post("admin/usuarios");
        const user = (usersResp?.data || []).find(u => u.id === btn.dataset.id);
        if (user) AdminController._showUserForm(user, categorias);
      });
    });

    // Eliminar usuario
    document.querySelectorAll(".btn-user-delete").forEach(btn => {
      btn.addEventListener("click", async() => {
        const result = await Swal.fire({
          title: "¿Eliminar usuario?",
          html: `<p>Vas a eliminar permanentemente a <strong>${escapeHtml(btn.dataset.nombre)}</strong>.<br>
            Esta acción no se puede deshacer.</p>`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        });
        if (!result.isConfirmed) return;
        await ApiService.post("admin/usuario/eliminar", { id: btn.dataset.id });
        btn.closest("tr")?.remove();
        Swal.fire({ icon: "success", title: "Usuario eliminado", toast: true, position: "top-end", timer: 2000, showConfirmButton: false });
      });
    });
  },

  _showUserForm: async(user, categorias) => {
    // Usuario de un proveedor externo: nombre, usuario y rol los define el proveedor
    // (el rol, con su permiso de administrador); acá solo se activa y se le dan categorías
    const externo = !!user && !!user.proveedor && user.proveedor !== "local";
    const etiqueta = escapeHtml(AppConfig.auth.label);
    // Por categoría: acceso (ve) y, opcionalmente, edición (gestiona su contenido).
    // Las categorías tipo api no tienen artículos: no se editan.
    const catsCheck = categorias.map(c => {
      const esAdmin = user?.rol === "admin";
      const checked = user && !esAdmin && (user.categorias || []).includes(c.id);
      const edita = checked && (user.categoriasEditables || []).includes(c.id);
      const editaSwitch = c.tipo === "api"
        ? ""
        : `<div class="form-check form-switch mb-0 ms-auto">
            <input class="form-check-input su-edita" type="checkbox" value="${c.id}" id="edit-${c.id}" ${edita ? "checked" : ""} ${checked ? "" : "disabled"}>
            <label class="form-check-label small text-muted" for="edit-${c.id}">Edita</label>
          </div>`;
      return `<div class="d-flex align-items-center gap-2 mb-1">
        <div class="form-check mb-0">
          <input class="form-check-input su-cat" type="checkbox" value="${c.id}" id="perm-${c.id}" ${checked ? "checked" : ""}>
          <label class="form-check-label small" for="perm-${c.id}">${escapeHtml(c.nombre)}</label>
        </div>
        ${editaSwitch}
      </div>`;
    }).join("");

    const activoSection = user
      ? `
      <div class="mb-3 d-flex align-items-center gap-3 flex-wrap">
        <label class="form-label fw-semibold mb-0">Estado</label>
        <div class="form-check form-switch mb-0">
          <input class="form-check-input" type="checkbox" id="su-activo" ${user.activo !== false ? "checked" : ""}>
          <label class="form-check-label small" for="su-activo" id="su-activo-label">${user.activo !== false ? "Activo" : "Inactivo"}</label>
        </div>
        ${externo
          ? ""
          : `<button type="button" id="su-btn-invite" class="btn btn-outline-secondary btn-sm ms-auto">
          <i class="fas fa-envelope me-1"></i>Reenviar invitación
        </button>`}
      </div>`
      : "";

    const { value: formValues } = await Swal.fire({
      title: user ? "Editar usuario" : "Nuevo usuario",
      width: 540,
      html: `
        <div class="text-start">
          <div class="mb-3">
            <label class="form-label fw-semibold">Nombre</label>
            <input id="su-nombre" class="form-control" value="${escapeHtml(user?.nombre || "")}" ${externo ? "disabled" : ""}>
          </div>
          <div class="mb-3">
            ${externo
              ? `<label class="form-label fw-semibold">Usuario de ${etiqueta}</label>
            <input id="su-usuario" class="form-control" value="${escapeHtml(user.usuario || "")}" disabled>`
              : `<label class="form-label fw-semibold">Email</label>
            <input id="su-email" type="email" class="form-control" value="${escapeHtml(user?.email || "")}" ${user ? "disabled" : ""}>`}
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Rol</label>
            <select id="su-rol" class="form-select" ${externo ? "disabled" : ""}>
              <option value="user"  ${!user || user.rol === "user" ? "selected" : ""}>Usuario</option>
              <option value="admin" ${user?.rol === "admin" ? "selected" : ""}>Administrador</option>
            </select>
            ${externo ? `<div class="form-text" id="su-rol-hint">El rol lo define ${etiqueta}: es administrador quien tiene el permiso de administrador de esta aplicación.</div>` : ""}
          </div>
          ${activoSection}
          <hr class="my-3">
          <p class="fw-semibold text-muted small mb-2">CATEGORÍAS CON ACCESO</p>
          <div id="su-perms-section">
            <p class="text-muted small mb-2" id="su-perms-hint">Seleccioná las categorías a las que este usuario puede acceder. Con <strong>Edita</strong>, además gestiona sus artículos y archivos.</p>
            <div id="su-cats-wrap">${catsCheck}</div>
          </div>
        </div>
      `,
      didOpen: () => {
        const rolSelect = document.getElementById("su-rol");
        const permsSection = document.getElementById("su-perms-section");
        const permsHint = document.getElementById("su-perms-hint");

        const togglePerms = () => {
          const isAdmin = rolSelect.value === "admin";
          permsSection.style.opacity = isAdmin ? "0.4" : "1";
          permsSection.querySelectorAll("input").forEach(i => { i.disabled = isAdmin; });
          if (permsHint) {
            permsHint.innerHTML = isAdmin
              ? "Los administradores tienen acceso total a todas las categorías."
              : "Seleccioná las categorías a las que este usuario puede acceder. Con <strong>Edita</strong>, además gestiona sus artículos y archivos.";
          }
          if (!isAdmin) syncEdita();
        };

        // "Edita" solo tiene sentido con acceso a la categoría
        const syncEdita = () => {
          permsSection.querySelectorAll(".su-cat").forEach(cb => {
            const sw = document.getElementById(`edit-${cb.value}`);
            if (!sw) return;
            sw.disabled = !cb.checked;
            if (!cb.checked) sw.checked = false;
          });
        };
        permsSection.addEventListener("change", (e) => { if (e.target.classList.contains("su-cat")) syncEdita(); });

        rolSelect.addEventListener("change", togglePerms);
        togglePerms();

        const activoCheck = document.getElementById("su-activo");
        const activoLabel = document.getElementById("su-activo-label");
        activoCheck?.addEventListener("change", () => {
          if (activoLabel) activoLabel.textContent = activoCheck.checked ? "Activo" : "Inactivo";
        });

        // Botón "Reenviar invitación" solo en edición
        document.getElementById("su-btn-invite")?.addEventListener("click", async() => {
          const btn = document.getElementById("su-btn-invite");
          btn.disabled = true;
          btn.innerHTML = '<i class="fas fa-circle-notch fa-spin me-1"></i>Enviando...';
          try {
            await ApiService.post("admin/usuario/invite", { id: user.id });
            btn.innerHTML = '<i class="fas fa-check me-1"></i>Enviado';
          } catch (err) {
            btn.disabled = false;
            btn.innerHTML = '<i class="fas fa-envelope me-1"></i>Reenviar invitación';
            Swal.showValidationMessage("No se pudo enviar: " + err.message);
          }
        });
      },
      showCancelButton: true,
      confirmButtonText: user ? "Guardar cambios" : "Crear usuario",
      cancelButtonText: "Cancelar",
      returnFocus: false,
      preConfirm: () => {
        const nombre = document.getElementById("su-nombre").value.trim();
        const email = user ? user.email : document.getElementById("su-email").value.trim();
        if (!nombre || (!email && !externo)) {
          Swal.showValidationMessage("Nombre y email son requeridos");
          return false;
        }
        const rol = document.getElementById("su-rol").value;
        const isAdmin = rol === "admin";
        const cats = isAdmin ? null : [...document.querySelectorAll(".su-cat:checked")].map(cb => cb.value);
        const editables = isAdmin ? [] : [...document.querySelectorAll(".su-edita:checked")].map(cb => cb.value).filter(id => cats.includes(id));
        const result = { nombre, email, rol, categorias: cats, editables };
        if (user) result.activo = document.getElementById("su-activo")?.checked !== false;
        return result;
      },
    });

    if (!formValues) return;

    const endpoint = user ? "admin/usuario/editar" : "admin/usuario/crear";
    const payload = user ? { ...formValues, id: user.id } : formValues;
    await ApiService.post(endpoint, payload);

    // Guardar permisos por separado (para producción el backend puede ignorar si ya vienen en el payload)
    if (user && formValues.categorias !== undefined) {
      await ApiService.post("admin/usuario/permisos", { id: user.id, categorias: formValues.categorias, editables: formValues.editables });
    }

    Swal.fire({
      icon: "success",
      title: user ? "Usuario actualizado" : "Invitación enviada",
      text: user ? undefined : `Se envió un email a ${formValues.email} para que establezca su contraseña.`,
      toast: !!user,
      position: !user ? "center" : "top-end",
      timer: user ? 2000 : undefined,
      showConfirmButton: !user,
      confirmButtonText: "Entendido",
    });
    AdminController.render();
  },
};
